import {
    ChangeDetectorRef,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ArmService,
    AuthenticationService,
    DossierMedicalService,
    EspaceClientService,
    GeolocalisationService,
} from '@kv-common-components/kx-services';
import { I18nPipe } from '@shared/pipes/i18n.pipe';
import { StyleService } from '@shared/services/style.service';
import moment from 'moment';
import { Observable, zip } from 'rxjs';
import { ISearchSuggest } from './ISearchSuggest';
import { DocumentListHelperService } from '@shared/services/document-list-helper.service';
import { AppointmentHelperService } from '@shared/services/appointment-helper.service';

@Component({
    selector: 'app-appointment-planner-component',
    templateUrl: './appointment-planner.component.html',
    styleUrls: ['./appointment-planner.component.scss'],
})
export class AppointmentPlannerComponent implements OnInit, OnDestroy {
    @ViewChild('searchSuggestsBox') searchSuggestsBox;
    @ViewChild('geolocLabel') geolocLabel;
    @ViewChild('mapComponent') mapComponent;
    @ViewChild('searchTextField') searchTextField;
    currentSearchAddress: string;
    defaultUserAddress: any;
    listCenters: any[] = [];
    listIdApiPraticien: any[] = [];
    nbrOfCentersToShow = 5;
    disponibiliteSelected = true;
    mapSelected = false;
    distanceSelected = false;
    telephone: string;
    idAia: any;
    idDossierMedical: string;
    localisationButton: ISearchSuggest;
    recentSearches: ISearchSuggest[] = [];
    searchSuggests: ISearchSuggest[] = [];
    showSearchSuggests = false;
    showGeolocAutorizationModal = false;
    geolocApiLoaded = false;
    currentEspaceClient: any;
    centersLoaded = false;
    listCentersDistanceArounded: any[] = [];
    rendezVousByCenters: any[] = [];
    position: any;
    selectedCenter: any;
    recentSearchBundle = this.i18nPipe.transform(
        'appointment.appointment-planner.search.dropdown.recentssearch'
    );
    listeRendezVousMedical: any[] = [];

    constructor(
        private geolocService: GeolocalisationService,
        private authService: AuthenticationService,
        private espCltService: EspaceClientService,
        private styleService: StyleService,
        public armService: ArmService,
        private dossierMedicalService: DossierMedicalService,
        private i18nPipe: I18nPipe,
        public changeDetectorRef: ChangeDetectorRef,
        public appointmentHelperService: AppointmentHelperService
    ) {}
    @HostListener('document:click', ['$event.target'])
    toggleSearchSuggests(targetElement: any, show?: boolean): void {
        if (show) {
            if (this.localisationButton) {
                this.localisationButton.selected = false;
            }
            this.showSearchSuggests = true;
        } else {
            const clickedInsideSuggestsBox =
                this.searchSuggestsBox &&
                this.searchSuggestsBox.nativeElement.contains(targetElement);
            const clickedInsideGeolocLabel =
                this.geolocLabel &&
                this.geolocLabel.nativeElement.contains(targetElement);
            if (!clickedInsideSuggestsBox && !clickedInsideGeolocLabel) {
                this.showSearchSuggests = false;
            }
        }
    }
    ngOnInit(): void {
        this.styleService.updatePageStyle('add', 'colored');
        this.styleService.updatePageStyle('add', 'appointment-planner-page');
        const idPersonne = this.authService.currentUserValue.idPersonne;
        this.espCltService
            .getEspaceClient(idPersonne)
            .subscribe((espaceClient) => {
                this.idAia = espaceClient.personne.idAia;
                const start = String(espaceClient.personne.telephone).substring(
                    0,
                    2
                );
                const end = String(espaceClient.personne.telephone).substring(
                    espaceClient.personne.telephone.length - 2
                );
                const maskedCharacter = espaceClient.personne.telephone
                    .substring(2, espaceClient.personne.telephone.length - 2)
                    .split('')
                    .join(' ')
                    .replace(/\d/g, '*');
                this.telephone = start + ' ' + maskedCharacter + ' ' + end;
                this.currentEspaceClient = espaceClient;
                this.defaultUserAddress = {
                    street: `${espaceClient.personne.adresse1}, `,
                    city: `${espaceClient.personne.codePostal} ${espaceClient.personne.ville}`,
                };
                this.resetSearchSuggests();
                this.geolocService
                    .loadGeolocApi(DocumentListHelperService.geolocApiUrl)
                    .subscribe((loaded) => {
                        this.geolocApiLoaded = loaded;
                        this.geolocService
                            .addressToGpsCoordinates(
                                `${this.defaultUserAddress.street}${this.defaultUserAddress.city}`
                            )
                            .subscribe((resp) => {
                                this.currentSearchAddress =
                                    resp.formatted_address;
                                this.refreshPageData(
                                    resp.geometry.location.toJSON()
                                );
                            });
                    });
            });
    }

    ngOnDestroy(): void {
        this.styleService.updatePageStyle('remove', 'colored');
        this.styleService.updatePageStyle('remove', 'appointment-planner-page');
    }

    onSearchButtonClick() {
        this.geolocService
            .addressToGpsCoordinates(this.searchTextField.nativeElement.value)
            .subscribe((resp) => {
                this.currentSearchAddress = resp.formatted_address;
                this.refreshPageData(resp.geometry.location.toJSON());

                const adresseEnteredIsNotDefaultAdresse =
                    this.searchTextField.nativeElement.value !==
                    this.defaultUserAddress.street +
                        '' +
                        this.defaultUserAddress.city;
                const adresseEnteredNotYetSaved = !this.recentSearches.includes(
                    this.searchTextField.nativeElement.value
                );

                if (
                    this.recentSearches.length === 3 &&
                    adresseEnteredIsNotDefaultAdresse &&
                    adresseEnteredNotYetSaved
                ) {
                    this.recentSearches.pop();
                }
                if (
                    adresseEnteredIsNotDefaultAdresse &&
                    adresseEnteredNotYetSaved
                ) {
                    this.recentSearches.unshift(
                        this.searchTextField.nativeElement.value
                    );
                }
                this.searchTextField.nativeElement.value = '';
            });
    }
    selectDefaultUserAddress() {
        this.showSearchSuggests = false;
        this.searchTextField.nativeElement.value =
            this.defaultUserAddress.street + '' + this.defaultUserAddress.city;
    }

    initAutoCompletion() {
        this.changeDetectorRef.detectChanges();
        const googleMapSearchBox = new google.maps.places.Autocomplete(
            this.searchTextField.nativeElement,
            { componentRestrictions: { country: 'fr' } }
        );
        google.maps.event.addListener(
            googleMapSearchBox,
            'place_changed',
            () => {
                this.showSearchSuggests = false;
            }
        );
    }

    resetSearchSuggests(): void {
        this.localisationButton = {
            bundleName: this.i18nPipe.transform(
                'appointment.appointment-planner.search.dropdown.position'
            ),
            icon: 'assets/images/blue-geo.svg',
            selected: false,
        };
        this.searchSuggests = [
            this.localisationButton,
            {
                bundleName: this.i18nPipe.transform(
                    'appointment.appointment-planner.search.dropdown.mainaddress'
                ),
                addresses: [this.defaultUserAddress],
            },
            {
                bundleName: this.i18nPipe.transform(
                    'appointment.appointment-planner.search.dropdown.recentssearch'
                ),
                addresses: this.recentSearches,
            },
        ];
    }

    selectSuggestSearch(address: any) {
        this.searchTextField.nativeElement.value = address;
        this.showSearchSuggests = false;
    }

    handleGeolocButtonClick(searchItem: any): void {
        if (searchItem === this.localisationButton) {
            this.showSearchSuggests = false;
            this.showGeolocAutorizationModal = true;
        }
    }

    showMoreCenters() {
        if (this.listCenters.length - this.nbrOfCentersToShow >= 5) {
            this.nbrOfCentersToShow = this.nbrOfCentersToShow + 5;
        } else {
            this.nbrOfCentersToShow =
                this.nbrOfCentersToShow +
                (this.listCenters.length - this.nbrOfCentersToShow);
        }
    }

    scrollToMap() {
        window.scrollTo(0, document.body.scrollHeight);
        this.mapSelected = true;
        this.distanceSelected = false;
        this.disponibiliteSelected = false;
    }

    updateMenuSelection(menuSelectionNav: string) {
        if (menuSelectionNav === 'disponibiliteSelected') {
            this.disponibiliteSelected = true;
            this.distanceSelected = false;
            this.mapSelected = false;
            this.listCenters = this.rendezVousByCenters.map((t1) => ({
                ...t1,
                ...this.listCentersDistanceArounded.find(
                    (t2) => t2.id === t1.idApi
                ),
            }));
        } else {
            this.distanceSelected = true;
            this.disponibiliteSelected = false;
            this.mapSelected = false;
            this.listCenters.sort(
                (a, b) => a.distanceClient - b.distanceClient
            );
        }
    }

    initCenters(idAia: string, idDossier: string, position: any) {
        this.dossierMedicalService
            .getIdDossierMedical(idAia, idDossier)
            .subscribe((dossierMedical) => {
                this.idDossierMedical = dossierMedical.idDossierMedical;
                this.armService
                    .getDossierMedical(this.idDossierMedical)
                    .subscribe((resultDossMedicalAndRDV) => {
                        this.listeRendezVousMedical =
                            resultDossMedicalAndRDV.listeRendezVousMedical;
                    });
                this.setListCenters(position);
            });
    }

    setListCenters(position: any) {
        const results: Observable<any>[] = [];
        const listIdApiPraticien = [];
        const listRendezVousMedical = [];
        this.armService
            .rechercheRendezVous(
                this.idDossierMedical,
                position.lat,
                position.lng
            )
            .subscribe((rendezVous) => {
                if (rendezVous.listeResultatRecherche.length > 0) {
                    rendezVous.listeResultatRecherche.forEach(
                        (resulatRecherche) => {
                            listIdApiPraticien.push(
                                resulatRecherche.idApiPraticien
                            );
                            listRendezVousMedical.push({
                                id: resulatRecherche.idApiPraticien,
                                rendezVous:
                                    resulatRecherche.listeRendezVousMedical,
                                distanceClient: resulatRecherche.distanceClient,
                            });
                        }
                    );
                    listIdApiPraticien.forEach((idPraticien) => {
                        results.push(this.armService.getPraticien(idPraticien));
                    });
                    zip(...results).subscribe((centers) => {
                        this.orderCentersbyAvailability(
                            centers,
                            listRendezVousMedical
                        );
                        this.centersLoaded = true;
                        this.changeDetectorRef.detectChanges();
                        // When list centers is ready we refresh the map
                        this.mapComponent.refreshMapData(
                            this.position,
                            this.listCenters
                        );
                        this.initAutoCompletion();
                    });
                } else {
                    this.listCenters = [];
                    this.centersLoaded = true;
                    this.mapComponent.refreshMapData(
                        this.position,
                        this.listCenters
                    );
                    this.initAutoCompletion();
                }
            });
    }

    orderCentersbyAvailability(centers: any, listRendezVousMedical: any[]) {
        const listRendezVousAvailable = [];
        listRendezVousMedical.forEach((element) => {
            this.listCentersDistanceArounded.push({
                id: element.id,
                distanceClient: Math.round(element.distanceClient),
            });
            listRendezVousAvailable.push({
                id: element.id,
                rendezVous: element.rendezVous,
                rendezVousSortedByDate:
                    this.appointmentHelperService.getAppointmentsSortedByDate(
                        element
                    ),
            });
        });
        this.rendezVousByCenters = centers.map((t1) => ({
            ...t1,
            ...listRendezVousAvailable.find((t2) => t2.id === t1.idApi),
        }));
        this.rendezVousByCenters.sort((a, b) => {
            let centersOrderByDate = 0;
            if (a.rendezVous.length > 0 && b.rendezVous.length > 0) {
                const dateRendezVousA = moment(
                    a.rendezVous[0].dateDebut,
                    'YYYY-MM-DDThh:mm:ss'
                );
                const dateRendezVousB = moment(
                    b.rendezVous[0].dateDebut,
                    'YYYY-MM-DDThh:mm:ss'
                );
                centersOrderByDate = dateRendezVousA.diff(dateRendezVousB);
            }
            return (
                centersOrderByDate ||
                b.priseRendezVousEnLigne - a.priseRendezVousEnLigne ||
                a.rendezVous.length - b.rendezVous.length
            );
        });
        this.listCenters = this.rendezVousByCenters.map((t1) => ({
            ...t1,
            ...this.listCentersDistanceArounded.find(
                (t2) => t2.id === t1.idApi
            ),
        }));
    }

    handleCenterClick(center: any): void {
        this.selectedCenter = center;
        // call child component map function to emulate marker click
        this.mapComponent.handleMarkerClick(center, null, false);
    }

    authorizeGelocalization(): void {
        this.showGeolocAutorizationModal = false;
        this.localisationButton.selected = true;
        this.currentSearchAddress = this.localisationButton.bundleName;
        this.geolocService
            .localizeUser(
                this.currentEspaceClient.personne.idAia,
                String(this.authService.currentUserValue.idDossier)
            )
            .subscribe((position) => {
                this.refreshPageData({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
            });
    }

    refreshPageData(position: google.maps.LatLngLiteral): void {
        this.position = position;
        this.initCenters(
            this.idAia,
            String(this.authService.currentUserValue.idDossier),
            position
        );
    }
    handleKeyDown($event: any) {
        if ($event.key === 'Enter') {
            $event.preventDefault();
            if (this.searchTextField.nativeElement.value.length > 0) {
                this.onSearchButtonClick();
            }
        }
    }
}
